<script setup>
import { ref, onMounted } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'vuex';
import { computed } from 'vue';
import axios from 'axios';
import SectionLayout from './SectionLayout.vue';

const store = useStore();
const { currentAccount } = useAccount();

const currentUser = computed(() => store.getters['auth/getCurrentUser']);

const LARAVEL_URL = 'https://aceleres.ai/api/aurora/vagas-config';

const turmas    = ref([]);
const esgotadas = ref([]);
const loading   = ref(true);
const saving    = ref(false);
const savedMsg  = ref('');
const errorMsg  = ref('');

function headers() {
  return {
    'X-Chatwoot-Token':      currentUser.value?.access_token || '',
    'X-Chatwoot-Account-Id': currentAccount.value?.id || '',
  };
}

async function carregar() {
  loading.value  = true;
  errorMsg.value = '';
  try {
    const resp = await axios.get(LARAVEL_URL, { headers: headers() });
    turmas.value    = resp.data.turmas    || [];
    esgotadas.value = resp.data.esgotadas || [];
  } catch {
    // Config não disponível para esta conta — oculta silenciosamente
    turmas.value = [];
  } finally {
    loading.value = false;
  }
}

function isEsgotada(num) {
  return esgotadas.value.includes(num);
}

function toggleTurma(num) {
  esgotadas.value = isEsgotada(num)
    ? esgotadas.value.filter(n => n !== num)
    : [...esgotadas.value, num];
}

async function salvar() {
  saving.value   = true;
  savedMsg.value = '';
  errorMsg.value = '';
  try {
    await axios.patch(
      LARAVEL_URL,
      { esgotadas: esgotadas.value },
      { headers: headers() }
    );
    savedMsg.value = 'Salvo!';
    setTimeout(() => { savedMsg.value = ''; }, 3000);
  } catch {
    errorMsg.value = 'Erro ao salvar. Verifique se você tem permissão de administrador.';
  } finally {
    saving.value = false;
  }
}

onMounted(carregar);
</script>

<template>
  <SectionLayout
    v-if="!loading && (turmas.length > 0 || errorMsg)"
    title="Aurora IA — Vagas"
    description="Quando uma turma esgotar as vagas, ative o alerta. A Aurora passará a transferir o lead para um consultor humano em vez de enviar o link de checkout."
    with-border
  >
    <div class="flex flex-col gap-3 mt-2">
      <div
        v-for="turma in turmas"
        :key="turma.num"
        class="flex items-center justify-between rounded-lg border border-slate-200 p-3"
      >
        <div class="flex-1 pr-4">
          <p class="text-sm font-medium text-slate-800">{{ turma.nome }}</p>
          <p
            class="text-xs mt-0.5"
            :class="isEsgotada(turma.num) ? 'text-red-600 font-semibold' : 'text-slate-400'"
          >
            {{ isEsgotada(turma.num) ? '🔴 Vagas esgotadas — transfere para consultor' : '🟢 Vagas disponíveis — envia link de checkout' }}
          </p>
        </div>
        <button
          type="button"
          class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none"
          :class="isEsgotada(turma.num) ? 'bg-red-500' : 'bg-slate-200'"
          @click="toggleTurma(turma.num)"
        >
          <span
            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200"
            :class="isEsgotada(turma.num) ? 'translate-x-5' : 'translate-x-0'"
          />
        </button>
      </div>

      <div class="flex items-center gap-3 mt-1">
        <button
          type="button"
          :disabled="saving"
          class="rounded-md bg-woot-500 px-4 py-2 text-sm font-medium text-white hover:bg-woot-600 disabled:opacity-50 cursor-pointer"
          @click="salvar"
        >
          {{ saving ? 'Salvando...' : 'Salvar' }}
        </button>
        <span v-if="savedMsg" class="text-sm text-green-600 font-medium">{{ savedMsg }}</span>
        <span v-if="errorMsg" class="text-sm text-red-600 text-xs">{{ errorMsg }}</span>
      </div>
    </div>
  </SectionLayout>
</template>
